import React, { useState } from 'react';
import { useThroughputData } from '../hooks/useThroughputData';
import { FilterBar } from '../components/throughput/FilterBar';
import { KPIStrip } from '../components/throughput/KPIStrip';
import { ThroughputFunnel } from '../components/throughput/ThroughputFunnel';
import { LoanOfficerTable } from '../components/throughput/LoanOfficerTable';
import { LeadSourceBreakdown } from '../components/throughput/LeadSourceBreakdown';
import { DrillDownModal } from '../components/throughput/DrillDownModal';
import { FunnelStep, LeadSource } from '../types/throughput';
import { Loader2, TrendingUp } from 'lucide-react';
export function ThroughputDashboard() {
  const {
    isLoading,
    filters,
    setFilters,
    metrics,
    officers,
    filteredLeads,
    filteredOpportunities
  } = useThroughputData();
  const [drillDownState, setDrillDownState] = useState<{
    isOpen: boolean;
    step: FunnelStep | 'All';
    title: string;
  }>({
    isOpen: false,
    step: 'All',
    title: ''
  });
  const [selectedSource, setSelectedSource] = useState<LeadSource | null>(null);
  const handleRefresh = () => {
    window.location.reload();
  };
  const handleDrillDown = (step: string) => {
    setDrillDownState({
      isOpen: true,
      step: step as FunnelStep,
      title: `${step} Drill-down`
    });
  };
  const handleRowClick = (officerId: string) => {
    setFilters({
      ...filters,
      scopeType: 'Loan Officer',
      selectedScopeId: officerId
    });
  };
  const handleSourceClick = (source: LeadSource) => {
    if (selectedSource === source) {
      setSelectedSource(null);
      setFilters({
        ...filters,
        leadSources: []
      });
    } else {
      setSelectedSource(source);
      setFilters({
        ...filters,
        leadSources: [source]
      });
    }
  };
  if (isLoading || !metrics) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5]" data-id="element-304">
        <div className="flex flex-col items-center" data-id="element-305">
          <div className="w-12 h-12 rounded-xl bg-[#0176D3]/10 flex items-center justify-center mb-4" data-id="element-306">
            <Loader2 className="h-6 w-6 animate-spin text-[#0176D3]" data-id="element-307" />
          </div>
          <p className="text-sm text-[#706E6B]" data-id="element-308">Loading dashboard...</p>
        </div>
      </div>;
  }
  const drillDownLeads = drillDownState.step === 'All' || drillDownState.step === 'Created' ? filteredLeads : filteredLeads.filter(l => l.isConverted);
  const drillDownOpps = drillDownState.step === 'All' || drillDownState.step === 'Created' ? filteredOpportunities : filteredOpportunities.filter(o => {
    if (drillDownState.step === 'Converted') return true;
    if (drillDownState.step === 'Funded') return o.fundedDate;
    return o.stage === drillDownState.step;
  });
  return <div className="min-h-screen bg-[#F5F5F5]" data-id="element-309">
      <FilterBar filters={filters} setFilters={setFilters} officers={officers} onRefresh={handleRefresh} data-id="element-310" />

      <main className="max-w-[1600px] mx-auto px-6 py-6" data-id="element-311">
        {/* Page Header */}
        <div className="mb-6" data-id="element-312">
          <div className="flex items-center gap-3 mb-1" data-id="element-313">
            <div className="w-8 h-8 rounded-lg bg-[#0176D3]/10 flex items-center justify-center" data-id="element-314">
              <TrendingUp className="h-4 w-4 text-[#0176D3]" data-id="element-315" />
            </div>
            <h1 className="text-xl font-bold text-[#181818]" data-id="element-316">
              Lead → Funded Throughput
            </h1>
          </div>
          <p className="text-sm text-[#706E6B] ml-11" data-id="element-317">
            Cohort analysis of lead conversion performance
          </p>
        </div>

        {/* KPI Strip */}
        <KPIStrip metrics={metrics} onDrillDown={handleDrillDown} data-id="element-318" />

        {/* Lead Source Breakdown (conditional) */}
        {filters.groupBySource && <LeadSourceBreakdown leads={filteredLeads} opportunities={filteredOpportunities} onSourceClick={handleSourceClick} selectedSource={selectedSource} data-id="element-319" />}

        {/* Funnel */}
        <ThroughputFunnel metrics={metrics.funnelMetrics} onStepClick={handleDrillDown} data-id="element-320" />

        {/* Officer Table */}
        <LoanOfficerTable metrics={metrics.officerMetrics} onRowClick={handleRowClick} data-id="element-321" />
      </main>

      {/* Drill-down Modal */}
      <DrillDownModal isOpen={drillDownState.isOpen} onClose={() => setDrillDownState(prev => ({
      ...prev,
      isOpen: false
    }))} title={drillDownState.title} leads={drillDownLeads} opportunities={drillDownOpps} step={drillDownState.step} data-id="element-322" />
    </div>;
}